<template>
  <div class="bg-grey-lighten-2 printable-po-preview">
    <qtm-skeleton v-if="loading" />
    <div v-else-if="pdf" class="py-1">
      <vue-pdf-embed v-bind="pdfEmbedAttrs" :page="1" @loaded="onLoad" />
      <div v-if="additionalPages">
        <vue-pdf-embed v-for="i in additionalPages" :key="i" v-bind="pdfEmbedAttrs" :page="i + 1" />
      </div>
    </div>
    <v-alert v-else type="error">
      Failed to fetch purchase order PDF
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'
import type { Order } from '@quotetome/materials-api'

export interface Props {
  changeOrderData?: object,
  company?: boolean,
  id?: number,
  poData?: object,
  quote?: boolean,
  order?: Order,
}

const props = withDefaults(defineProps<Props>(), {
  changeOrderData: undefined,
  id: undefined,
  poData: undefined,
  order: undefined,
})

const loading = ref(true)
const pdf = ref()
const additionalPages = ref(0)

const pdfEmbedAttrs = computed(() => ({
  class: 'elevation-1 ma-3',
  disableAnnotationLayer: true,
  disableTextLayer: true,
  source: pdf.value,
}))

onBeforeUnmount(() => {
  if (pdf.value) {
    window.URL.revokeObjectURL(pdf.value)
  }
})

const { $api, $error } = useNuxtApp()
const fetchPdf = async () => {
  let apiMethod = $api.v1.purchaseOrders.pdf
  let data
  let id = props.id

  loading.value = true
  if (props.poData) {
    apiMethod = $api.v1.previewPurchaseOrders.create
    data = props.poData
  }
  else if (props.changeOrderData) {
    apiMethod = $api.v1.previewPurchaseOrders.previewChangeOrderPO
    data = props.changeOrderData
  }
  else if (props.company) {
    apiMethod = $api.v1.companies.previewPO
  }
  else if (props.order) {
    id = props.order.winningQuotes[0]?.purchase_order
  }
  else if (props.quote) {
    apiMethod = $api.v1.quotes.previewPO
  }

  try {
    let pdfBlob
    if (id) {
      pdfBlob = await apiMethod(id, data)
    }
    else if (data) {
      pdfBlob = await apiMethod(data)
    }
    pdf.value = window.URL.createObjectURL(pdfBlob)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const onLoad = (pdfProxy: any) => {
  additionalPages.value = pdfProxy.numPages - 1
}

onMounted(fetchPdf)
</script>
